import { useCallback, useEffect, useState } from 'react';

/**
 * Browser fullscreen, which is the only kind an Activity can ask for itself.
 *
 * The Discord SDK has no fullscreen, pop-out or resize command — layout is
 * read-only there (`ACTIVITY_LAYOUT_MODE_UPDATE` reports the mode, nothing sets
 * it), so the frame Discord hands us is the frame we get. What is left is the
 * platform's own Fullscreen API, and that only works if the host iframe carries
 * `allow="fullscreen"`.
 *
 * We can't know that in advance, so `supported` reports `document.fullscreenEnabled`
 * — the spec's own answer to "may this document go fullscreen", which already
 * accounts for the host iframe's `allow` attribute — and the caller hides the
 * control when it's false, rather than offering a button that does nothing.
 *
 * A request can still be refused afterwards, almost always because the click
 * didn't count as a user gesture. That is transient, so it is swallowed rather
 * than used to hide the control: a mistimed click must not remove the button for
 * the rest of the session.
 */
export interface FullscreenState {
  /** Whether asking for fullscreen is worth offering at all. */
  supported: boolean;
  active: boolean;
  toggle: () => void;
}

export function useFullscreen(): FullscreenState {
  const supported = typeof document !== 'undefined' && document.fullscreenEnabled;
  const [active, setActive] = useState(false);

  useEffect(() => {
    // Discord's own fullscreen control and the Escape key both change this
    // without going through us, so the icon follows the document, not the click.
    const sync = () => setActive(document.fullscreenElement !== null);
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggle = useCallback(() => {
    // Swallowed, not surfaced: `fullscreenchange` is what drives the icon, so a
    // refused request simply leaves the button where it was.
    const ignore = () => {};

    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(ignore);
      return;
    }
    void document.documentElement.requestFullscreen().catch(ignore);
  }, []);

  return { supported, active, toggle };
}
